// Sign-in with a personal account: user name and password, then – with two-factor authentication –
// the code from the authenticator app (or a recovery code) as a second step.
import { useEffect, useRef, useState } from 'react';
import type { MeResponse } from '../../../shared/types';
import { login } from '../lib/api';
import { ApiClientError } from '../lib/http';
import { CodeInput, codeLooksComplete, FormError, PasswordInput } from '../components/AccountForms';
import { Icon } from '../components/Icon';

const MSG_OFFLINE = 'Ni povezave s strežnikom. Preverite internetno povezavo in poskusite znova.';

function loginErrorMessage(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.status === 0) return MSG_OFFLINE;
    if (err.status === 429) return err.message || 'Preveč poskusov. Poskusite čez nekaj minut.';
    if (err.code === 'invalid_credentials') return err.message || 'Napačno uporabniško ime ali geslo.';
    if (err.code === 'invalid_code') return err.message || 'Koda ni pravilna.';
    return err.message || 'Prijava ni uspela.';
  }
  return 'Prijava ni uspela.';
}

interface LoginPageProps {
  /** The session ended while the app was in use. */
  expired?: boolean;
  /** Shown over the still open page (see App's LoginOverlay). */
  overlay?: boolean;
  /** Pre-filled user name (the user whose session ended). */
  defaultUsername?: string;
  onSuccess?: (me: MeResponse) => void;
}

export function LoginPage({ expired, overlay, defaultUsername = '', onSuccess }: LoginPageProps) {
  const [username, setUsername] = useState(defaultUsername);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  /** The password was accepted; the second factor is asked for. */
  const [needCode, setNeedCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (needCode) codeRef.current?.focus();
    else if (defaultUsername) passwordRef.current?.focus();
    else usernameRef.current?.focus();
  }, [needCode, defaultUsername]);

  const canSubmit = needCode ? codeLooksComplete(code, true) : username.trim() !== '' && password !== '';

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      const me = await login({ username, password, ...(needCode ? { code } : {}) });
      setPassword('');
      setCode('');
      onSuccess?.(me);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'code_required') {
        setNeedCode(true);
        return;
      }
      setError(loginErrorMessage(err));
      if (needCode && err instanceof ApiClientError && err.code === 'invalid_credentials') {
        // The account changed meanwhile (e.g. password reset): start again.
        setNeedCode(false);
        setCode('');
        setPassword('');
      } else if (needCode) {
        codeRef.current?.select();
      } else {
        passwordRef.current?.select();
      }
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    setNeedCode(false);
    setCode('');
    setPassword('');
    setError(null);
  };

  let intro: string;
  if (needCode) intro = 'Odprite aplikacijo za preverjanje na telefonu in vpišite 6-mestno kodo za BAAM Transkripcijo.';
  else if (overlay)
    intro = 'Prijava je potekla. Prijavite se znova – morebitno snemanje teče naprej, neposlane spremembe pa bodo poslane po prijavi.';
  else if (expired) intro = 'Prijava je potekla. Prijavite se znova – neposlane spremembe so shranjene v napravi in bodo poslane po prijavi.';
  else intro = 'Prijavite se s svojim uporabniškim imenom in geslom.';

  return (
    <div className="page page--center page--login">
      <form
        className="card login"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <span className="login__icon" aria-hidden="true">
          <Icon name="lock" size={28} />
        </span>
        <h1 id="login-title" className="login__title">
          {needCode ? 'Dvostopenjska prijava' : 'Prijava'}
        </h1>
        <p className="muted">{intro}</p>
        {needCode ? (
          <>
            <CodeInput
              id="login-code"
              label="Koda iz aplikacije za preverjanje (ali obnovitvena koda)"
              value={code}
              onChange={setCode}
              inputRef={codeRef}
              allowRecovery
              invalid={error !== null}
              describedBy={error ? 'login-error' : 'login-code-hint'}
            />
            <p id="login-code-hint" className="muted small">
              Brez telefona? Vpišite eno od obnovitvenih kod (npr. K7QM-2XWD-9PRT-HC4N), ki ste jih shranili ob vklopu – vsaka velja le enkrat.
            </p>
          </>
        ) : (
          <>
            <div className="field">
              <label htmlFor="username">Uporabniško ime</label>
              <input
                ref={usernameRef}
                id="username"
                type="text"
                autoComplete="username"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                aria-invalid={error ? true : undefined}
                enterKeyHint="next"
              />
            </div>
            <PasswordInput
              id="password"
              label="Geslo"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              inputRef={passwordRef}
              invalid={error !== null}
              describedBy={error ? 'login-error' : undefined}
              enterKeyHint="go"
            />
          </>
        )}
        {error && <FormError id="login-error">{error}</FormError>}
        <button type="submit" className="btn btn--primary btn--block" disabled={!canSubmit || busy}>
          {busy ? <Icon name="spinner" size={20} /> : null} {needCode ? 'Potrdi' : 'Prijava'}
        </button>
        {needCode && (
          <button type="button" className="btn btn--ghost btn--block" onClick={back} disabled={busy}>
            <Icon name="chevronLeft" size={20} /> Nazaj
          </button>
        )}
        {!needCode && <p className="muted small login__help">Ste pozabili geslo? Novo začasno geslo vam nastavi skrbnik aplikacije.</p>}
      </form>
    </div>
  );
}
